import { openPencilColorToHex, type OpenPencilRgbColor } from "./fill.ts";

/**
 * Scene-graph parity oracle.
 *
 * Graph node ids are assigned from a process-global counter at parse time, so
 * they differ between two parses of identical bytes. Parity keys therefore use
 * the retained Figma source identity when present and otherwise a structural
 * path (parent key / type@sibling-index) that is stable across parses. Node
 * names are deliberately NOT part of the structural key so a bounded
 * rename-node edit keys to the same node before and after.
 *
 * The tracked field set below is the parity contract: every field the bounded
 * edit surface can touch plus the visually meaningful mutable state
 * (fills/strokes/effects, text + style runs, typography, auto-layout, corner
 * radii, blend mode, component property values). Engine-derived caches
 * (figmaDerivedLayout, vector network blobs) are intentionally excluded.
 */

interface OpenPencilParityFillLike {
  type: string;
  color?: OpenPencilRgbColor | null;
  opacity?: number;
  visible?: boolean;
  blendMode?: string;
  gradientStops?: Array<{ color: OpenPencilRgbColor; position: number }>;
  imageHash?: string;
  imageScaleMode?: string;
}

interface OpenPencilParityStrokeLike {
  color: OpenPencilRgbColor;
  weight: number;
  opacity?: number;
  visible?: boolean;
  align?: string;
  cap?: string;
  join?: string;
  dashPattern?: number[];
}

interface OpenPencilParityEffectLike {
  type: string;
  color: OpenPencilRgbColor;
  offset: { x: number; y: number };
  radius: number;
  spread?: number;
  visible?: boolean;
  blendMode?: string;
}

export interface OpenPencilParitySceneNodeLike {
  id: string;
  type: string;
  name: string;
  parentId: string | null;
  childIds: string[];
  source?: { id?: string | null } | null;
  visible: boolean;
  locked?: boolean;
  opacity: number;
  blendMode?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  clipsContent?: boolean;
  cornerRadius?: number;
  topLeftRadius?: number;
  topRightRadius?: number;
  bottomRightRadius?: number;
  bottomLeftRadius?: number;
  independentCorners?: boolean;
  cornerSmoothing?: number;
  text: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number;
  italic?: boolean;
  textAlignHorizontal?: string;
  textAlignVertical?: string;
  textAutoResize?: string;
  textCase?: string;
  textDecoration?: string;
  lineHeight?: number | null;
  letterSpacing?: number;
  styleRuns?: unknown[];
  fills?: OpenPencilParityFillLike[];
  strokes?: OpenPencilParityStrokeLike[];
  effects?: OpenPencilParityEffectLike[];
  layoutMode?: string;
  layoutWrap?: string;
  primaryAxisAlign?: string;
  counterAxisAlign?: string;
  primaryAxisSizing?: string;
  counterAxisSizing?: string;
  itemSpacing?: number;
  counterAxisSpacing?: number;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  layoutPositioning?: string;
  componentPropertyValues?: Record<string, string>;
  componentPropertyDefinitions?: unknown[];
}

export interface OpenPencilParitySceneGraphLike {
  getPages(includeAll: boolean): Array<{ id: string }>;
  getNode(id: string): OpenPencilParitySceneNodeLike | null | undefined;
  getAllNodes(): Iterable<OpenPencilParitySceneNodeLike>;
}

export interface SceneGraphNodeParityRecord {
  type: string;
  name: string;
  parentKey: string | null;
  childKeys: string[];
  sourceId: string | null;
  visible: boolean;
  locked: boolean;
  opacity: number;
  blendMode: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  clipsContent: boolean;
  cornerSignature: string;
  text: string;
  typographySignature: string;
  styleRunsSignature: string;
  fillsSignature: string;
  strokesSignature: string;
  effectsSignature: string;
  layoutSignature: string;
  componentSignature: string;
}

export interface SceneGraphNodeParityChange {
  parityKey: string;
  changedFields: string[];
  before: SceneGraphNodeParityRecord;
  after: SceneGraphNodeParityRecord;
}

export interface SceneGraphParityDiff {
  comparedNodeCount: number;
  missingParityKeys: string[];
  addedParityKeys: string[];
  changed: SceneGraphNodeParityChange[];
}

/**
 * Exact after-side expectation for ONE paint slot of a paint signature.
 *
 * `allowedFields` alone would only say "this node's fills may move"; this says
 * WHICH paint moved and WHAT colour it must land on. The check enforced in
 * {@link assertRevisionChangeIsolation} is therefore strictly stronger than a
 * field allowance: every other paint slot must be byte-identical, the declared
 * slot may differ only in `color`, and that colour must equal
 * {@link ExpectedPaintColorChange.afterColorHex}.
 */
export interface ExpectedPaintColorChange {
  field: "fillsSignature" | "strokesSignature";
  index: number;
  afterColorHex: string;
}

export interface ExpectedSceneGraphChange {
  parityKey: string;
  allowedFields: string[];
  /**
   * Optional per-paint exact expectations. When present, the allowed field is
   * NOT a blanket allowance — the after-side paint list must match these
   * declarations exactly (see {@link ExpectedPaintColorChange}).
   */
  expectedPaintColors?: ExpectedPaintColorChange[];
}

/**
 * `.fig` numeric fields are float32-backed: a reparse after export can shift a
 * value by ~1 ulp (e.g. lineHeight 23.799999594 → 23.799999237), and any
 * FIXED-decimal rounding lets two such neighbors straddle a rounding boundary.
 * Canonicalize to 6 SIGNIFICANT digits instead — safely inside float32's ~7.2
 * significant-digit precision at every magnitude.
 */
function roundParityNumber(value: number): number {
  if (!Number.isFinite(value) || value === 0) return value === 0 ? 0 : value;
  return Number(value.toPrecision(6));
}

function canonicalizeParityValue(value: unknown): unknown {
  if (typeof value === "number" && Number.isFinite(value)) {
    return roundParityNumber(value);
  }
  if (Array.isArray(value)) {
    return value.map(canonicalizeParityValue);
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const canonical: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      if (record[key] === undefined) continue;
      canonical[key] = canonicalizeParityValue(record[key]);
    }
    return canonical;
  }
  return value ?? null;
}

export function paritySignature(value: unknown): string {
  return JSON.stringify(canonicalizeParityValue(value));
}

function sourceIdentity(node: OpenPencilParitySceneNodeLike): string | null {
  const id = node.source?.id;
  return typeof id === "string" && id.trim().length > 0 ? id : null;
}

export function sceneGraphSourceParityKey(sourceId: string): string {
  return `src:${sourceId}`;
}

/**
 * Real Figma exports carry DUPLICATE source ids (component-instance internals
 * share their main-component node's identity; a 93K-node production file
 * collides on dozens of ids). A source id keys a node directly only when it is
 * unique in the document; duplicated ids fall back to the structural-path key
 * with a `#src=` marker so identity stays traceable and parse-stable.
 */
function countSourceIdentities(
  graph: OpenPencilParitySceneGraphLike,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of graph.getAllNodes()) {
    const source = sourceIdentity(node);
    if (source === null) continue;
    counts.set(source, (counts.get(source) ?? 0) + 1);
  }
  return counts;
}

function structuralOrSourceKey(
  node: OpenPencilParitySceneNodeLike,
  structuralKey: string,
  sourceCounts: Map<string, number>,
): string {
  const source = sourceIdentity(node);
  if (source === null) return structuralKey;
  if (sourceCounts.get(source) === 1) return sceneGraphSourceParityKey(source);
  return `${structuralKey}#src=${source}`;
}

function assignChildParityKeys(
  graph: OpenPencilParitySceneGraphLike,
  parent: OpenPencilParitySceneNodeLike,
  parentKey: string,
  keyByNodeId: Map<string, string>,
  sourceCounts: Map<string, number>,
): void {
  parent.childIds.forEach((childId, index) => {
    const child = graph.getNode(childId);
    if (!child) return;
    const childKey = structuralOrSourceKey(
      child,
      `${parentKey}/${child.type}@${index}`,
      sourceCounts,
    );
    keyByNodeId.set(child.id, childKey);
    assignChildParityKeys(graph, child, childKey, keyByNodeId, sourceCounts);
  });
}

export function buildSceneGraphParityKeyMap(
  graph: OpenPencilParitySceneGraphLike,
): Map<string, string> {
  const keyByNodeId = new Map<string, string>();
  const sourceCounts = countSourceIdentities(graph);
  graph.getPages(true).forEach((page, pageIndex) => {
    const pageNode = graph.getNode(page.id);
    if (!pageNode) return;
    const pageKey = structuralOrSourceKey(
      pageNode,
      `page:${pageNode.name}@${pageIndex}`,
      sourceCounts,
    );
    keyByNodeId.set(pageNode.id, pageKey);
    assignChildParityKeys(graph, pageNode, pageKey, keyByNodeId, sourceCounts);
  });
  let detachedIndex = 0;
  for (const node of graph.getAllNodes()) {
    if (keyByNodeId.has(node.id)) continue;
    keyByNodeId.set(
      node.id,
      structuralOrSourceKey(
        node,
        `detached:${node.type}@${detachedIndex}`,
        sourceCounts,
      ),
    );
    detachedIndex += 1;
  }
  return keyByNodeId;
}

function fillParityValue(fill: OpenPencilParityFillLike): unknown {
  return {
    type: fill.type,
    color: fill.color ? openPencilColorToHex(fill.color) : null,
    opacity: fill.opacity ?? 1,
    visible: fill.visible ?? true,
    blendMode: fill.blendMode ?? null,
    gradientStops: (fill.gradientStops ?? []).map((stop) => ({
      color: openPencilColorToHex(stop.color),
      position: stop.position,
    })),
    imageHash: fill.imageHash ?? null,
    imageScaleMode: fill.imageScaleMode ?? null,
  };
}

function strokeParityValue(stroke: OpenPencilParityStrokeLike): unknown {
  return {
    color: openPencilColorToHex(stroke.color),
    weight: stroke.weight,
    opacity: stroke.opacity ?? 1,
    visible: stroke.visible ?? true,
    align: stroke.align ?? null,
    cap: stroke.cap ?? null,
    join: stroke.join ?? null,
    dashPattern: stroke.dashPattern ?? [],
  };
}

function effectParityValue(effect: OpenPencilParityEffectLike): unknown {
  return {
    type: effect.type,
    color: openPencilColorToHex(effect.color),
    offset: effect.offset,
    radius: effect.radius,
    spread: effect.spread ?? 0,
    visible: effect.visible ?? true,
    blendMode: effect.blendMode ?? null,
  };
}

/**
 * `.fig` export materializes a text node's base typography into each style
 * run (`{textDecoration:"UNDERLINE"}` reparses as `{fontFamily:…, fontWeight:…,
 * italic:false, textDecoration:"UNDERLINE"}` when those values equal the node
 * base). Canonicalize runs to their SPARSE form — drop style entries equal to
 * the node's base value — so materialization is not drift while any value that
 * genuinely differs from the base still is.
 */
function sparseStyleRuns(node: OpenPencilParitySceneNodeLike): unknown[] {
  const base: Record<string, unknown> = {
    fontFamily: node.fontFamily ?? null,
    fontWeight: node.fontWeight ?? null,
    italic: node.italic ?? false,
    fontSize: node.fontSize ?? null,
    letterSpacing: node.letterSpacing ?? null,
    lineHeight: node.lineHeight ?? null,
    textDecoration: node.textDecoration ?? null,
    textCase: node.textCase ?? null,
  };
  const sparseRuns = (node.styleRuns ?? []).map((run) => {
    if (!run || typeof run !== "object") return run;
    const record = run as Record<string, unknown>;
    const style = record.style;
    if (!style || typeof style !== "object") return run;
    const sparse: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      style as Record<string, unknown>,
    )) {
      if (
        key in base &&
        paritySignature(value) === paritySignature(base[key])
      ) {
        continue;
      }
      sparse[key] = value;
    }
    return { ...record, style: sparse };
  });
  // Export also COALESCES adjacent runs whose styles are identical
  // ((144,+26)+(170,+12) reparses as (144,+38) on real files). Character
  // coverage and effective styling are unchanged, so canonicalize both sides
  // to the merged form; runs separated by a gap never merge.
  const merged: unknown[] = [];
  for (const run of sparseRuns) {
    const previous = merged[merged.length - 1] as
      | Record<string, unknown>
      | undefined;
    const current = run as Record<string, unknown> | null;
    if (
      previous &&
      current &&
      typeof previous.start === "number" &&
      typeof previous.length === "number" &&
      typeof current.start === "number" &&
      typeof current.length === "number" &&
      previous.start + previous.length === current.start &&
      paritySignature(previous.style) === paritySignature(current.style)
    ) {
      previous.length = previous.length + current.length;
      continue;
    }
    merged.push(
      current && typeof current === "object" ? { ...current } : run,
    );
  }
  return merged;
}

function nodeParityRecord(
  node: OpenPencilParitySceneNodeLike,
  keyByNodeId: Map<string, string>,
): SceneGraphNodeParityRecord {
  return {
    type: node.type,
    name: node.name,
    parentKey:
      node.parentId !== null
        ? (keyByNodeId.get(node.parentId) ?? null)
        : null,
    childKeys: node.childIds.map(
      (childId) => keyByNodeId.get(childId) ?? `unknown:${childId}`,
    ),
    sourceId: sourceIdentity(node),
    visible: node.visible,
    locked: node.locked ?? false,
    opacity: roundParityNumber(node.opacity),
    // The `.fig` writer one-directionally normalizes NORMAL → PASS_THROUGH
    // (observed on real files for leaves AND containers). On leaves the two
    // render identically; on containers Figma applies group opacity by
    // isolation either way, leaving backdrop-blending descendants as the only
    // residual difference — outside this oracle's geometry/structure/content
    // claim. The NORMAL/PASS_THROUGH pair is therefore ONE equivalence class;
    // every other blend mode stays strict.
    blendMode:
      node.blendMode === "PASS_THROUGH" || node.blendMode === "NORMAL"
        ? "NORMAL"
        : (node.blendMode ?? null),
    x: roundParityNumber(node.x),
    y: roundParityNumber(node.y),
    width: roundParityNumber(node.width),
    height: roundParityNumber(node.height),
    rotation: roundParityNumber(node.rotation),
    clipsContent: node.clipsContent ?? false,
    cornerSignature: paritySignature({
      cornerRadius: node.cornerRadius ?? 0,
      topLeftRadius: node.topLeftRadius ?? 0,
      topRightRadius: node.topRightRadius ?? 0,
      bottomRightRadius: node.bottomRightRadius ?? 0,
      bottomLeftRadius: node.bottomLeftRadius ?? 0,
      independentCorners: node.independentCorners ?? false,
      cornerSmoothing: node.cornerSmoothing ?? 0,
    }),
    text: node.text,
    typographySignature: paritySignature({
      fontSize: node.fontSize ?? null,
      fontFamily: node.fontFamily ?? null,
      fontWeight: node.fontWeight ?? null,
      italic: node.italic ?? false,
      textAlignHorizontal: node.textAlignHorizontal ?? null,
      textAlignVertical: node.textAlignVertical ?? null,
      textAutoResize: node.textAutoResize ?? null,
      textCase: node.textCase ?? null,
      textDecoration: node.textDecoration ?? null,
      lineHeight: node.lineHeight ?? null,
      letterSpacing: node.letterSpacing ?? null,
    }),
    styleRunsSignature: paritySignature(sparseStyleRuns(node)),
    fillsSignature: paritySignature((node.fills ?? []).map(fillParityValue)),
    strokesSignature: paritySignature(
      (node.strokes ?? []).map(strokeParityValue),
    ),
    effectsSignature: paritySignature(
      (node.effects ?? []).map(effectParityValue),
    ),
    layoutSignature: paritySignature({
      layoutMode: node.layoutMode ?? null,
      layoutWrap: node.layoutWrap ?? null,
      primaryAxisAlign: node.primaryAxisAlign ?? null,
      counterAxisAlign: node.counterAxisAlign ?? null,
      primaryAxisSizing: node.primaryAxisSizing ?? null,
      counterAxisSizing: node.counterAxisSizing ?? null,
      itemSpacing: node.itemSpacing ?? 0,
      counterAxisSpacing: node.counterAxisSpacing ?? 0,
      paddingTop: node.paddingTop ?? 0,
      paddingRight: node.paddingRight ?? 0,
      paddingBottom: node.paddingBottom ?? 0,
      paddingLeft: node.paddingLeft ?? 0,
      layoutPositioning: node.layoutPositioning ?? null,
    }),
    componentSignature: paritySignature({
      componentPropertyValues: node.componentPropertyValues ?? {},
      componentPropertyDefinitions: node.componentPropertyDefinitions ?? [],
    }),
  };
}

export function buildSceneGraphParitySnapshot(
  graph: OpenPencilParitySceneGraphLike,
): Map<string, SceneGraphNodeParityRecord> {
  const keyByNodeId = buildSceneGraphParityKeyMap(graph);
  const records = new Map<string, SceneGraphNodeParityRecord>();
  for (const node of graph.getAllNodes()) {
    const parityKey = keyByNodeId.get(node.id);
    if (!parityKey) continue;
    if (records.has(parityKey)) {
      throw new Error(
        `Scene graph parity key collision on ${parityKey}; the graph carries duplicate source identities.`,
      );
    }
    records.set(parityKey, nodeParityRecord(node, keyByNodeId));
  }
  return records;
}

function changedParityFields(
  before: SceneGraphNodeParityRecord,
  after: SceneGraphNodeParityRecord,
): string[] {
  const fields: string[] = [];
  for (const key of Object.keys(before) as Array<
    keyof SceneGraphNodeParityRecord
  >) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      fields.push(key);
    }
  }
  return fields;
}

export function diffSceneGraphParity(
  before: OpenPencilParitySceneGraphLike,
  after: OpenPencilParitySceneGraphLike,
): SceneGraphParityDiff {
  const beforeRecords = buildSceneGraphParitySnapshot(before);
  const afterRecords = buildSceneGraphParitySnapshot(after);
  const missingParityKeys = [...beforeRecords.keys()].filter(
    (parityKey) => !afterRecords.has(parityKey),
  );
  const addedParityKeys = [...afterRecords.keys()].filter(
    (parityKey) => !beforeRecords.has(parityKey),
  );
  const changed: SceneGraphNodeParityChange[] = [];
  for (const [parityKey, beforeRecord] of beforeRecords) {
    const afterRecord = afterRecords.get(parityKey);
    if (!afterRecord) continue;
    const changedFields = changedParityFields(beforeRecord, afterRecord);
    if (changedFields.length > 0) {
      changed.push({
        parityKey,
        changedFields,
        before: beforeRecord,
        after: afterRecord,
      });
    }
  }
  return {
    comparedNodeCount: beforeRecords.size,
    missingParityKeys,
    addedParityKeys,
    changed,
  };
}

function parsePaintSignature(
  parityKey: string,
  field: string,
  side: "before" | "after",
  signature: string,
): Array<Record<string, unknown>> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(signature);
  } catch {
    throw new Error(
      `Node ${parityKey} ${side} ${field} is not a decodable paint signature: ${signature}.`,
    );
  }
  if (!Array.isArray(parsed)) {
    throw new Error(
      `Node ${parityKey} ${side} ${field} is not a paint list: ${signature}.`,
    );
  }
  return parsed as Array<Record<string, unknown>>;
}

/**
 * Enforce exact per-paint expectations for a changed node (see
 * {@link ExpectedPaintColorChange}). Every declared slot must land on the
 * declared colour with everything else about that paint unchanged, and every
 * UNDECLARED slot must be byte-identical — so an allowance issued for a
 * variable re-materialization cannot be borrowed to hide an unrelated paint
 * edit on the same node.
 */
function assertExpectedPaintColors(
  change: SceneGraphNodeParityChange,
  expectations: ExpectedPaintColorChange[],
): void {
  if (expectations.length === 0) return;
  const byField = new Map<string, ExpectedPaintColorChange[]>();
  for (const expectation of expectations) {
    byField.set(expectation.field, [
      ...(byField.get(expectation.field) ?? []),
      expectation,
    ]);
  }
  for (const [field, fieldExpectations] of byField) {
    const beforePaints = parsePaintSignature(
      change.parityKey,
      field,
      "before",
      change.before[field as "fillsSignature" | "strokesSignature"],
    );
    const afterPaints = parsePaintSignature(
      change.parityKey,
      field,
      "after",
      change.after[field as "fillsSignature" | "strokesSignature"],
    );
    if (beforePaints.length !== afterPaints.length) {
      throw new Error(
        `Node ${change.parityKey} ${field} changed paint count ${beforePaints.length} → ${afterPaints.length}; a variable re-materialization may only change paint colours.`,
      );
    }
    const declared = new Map<number, ExpectedPaintColorChange>();
    for (const expectation of fieldExpectations) {
      if (
        expectation.index < 0 ||
        expectation.index >= afterPaints.length ||
        !Number.isInteger(expectation.index)
      ) {
        throw new Error(
          `Node ${change.parityKey} ${field} has no paint at expected index ${expectation.index}.`,
        );
      }
      declared.set(expectation.index, expectation);
    }
    for (let index = 0; index < afterPaints.length; index += 1) {
      const beforePaint = beforePaints[index] ?? {};
      const afterPaint = afterPaints[index] ?? {};
      const expectation = declared.get(index);
      if (!expectation) {
        if (paritySignature(beforePaint) !== paritySignature(afterPaint)) {
          throw new Error(
            `Node ${change.parityKey} ${field} changed paint ${index}, which no variable re-materialization accounts for: before ${JSON.stringify(beforePaint)}, after ${JSON.stringify(afterPaint)}.`,
          );
        }
        continue;
      }
      const observed =
        typeof afterPaint.color === "string"
          ? afterPaint.color.toLowerCase()
          : null;
      if (observed !== expectation.afterColorHex.toLowerCase()) {
        throw new Error(
          `Node ${change.parityKey} ${field} paint ${index} re-materialized to ${JSON.stringify(afterPaint.color)}, expected ${JSON.stringify(expectation.afterColorHex)}.`,
        );
      }
      const { color: _beforeColor, ...beforeRest } = beforePaint;
      const { color: _afterColor, ...afterRest } = afterPaint;
      if (paritySignature(beforeRest) !== paritySignature(afterRest)) {
        throw new Error(
          `Node ${change.parityKey} ${field} paint ${index} changed beyond its bound colour: before ${JSON.stringify(beforePaint)}, after ${JSON.stringify(afterPaint)}.`,
        );
      }
    }
  }
}

export function assertRevisionChangeIsolation(
  before: OpenPencilParitySceneGraphLike,
  after: OpenPencilParitySceneGraphLike,
  expectedChanges: ExpectedSceneGraphChange[],
): SceneGraphParityDiff {
  const diff = diffSceneGraphParity(before, after);
  if (diff.missingParityKeys.length > 0 || diff.addedParityKeys.length > 0) {
    throw new Error(
      `Revision changed graph membership: missing ${JSON.stringify(diff.missingParityKeys)}, added ${JSON.stringify(diff.addedParityKeys)}.`,
    );
  }
  const expectedByKey = new Map<string, Set<string>>();
  const expectedPaintsByKey = new Map<string, ExpectedPaintColorChange[]>();
  for (const change of expectedChanges) {
    const fields = expectedByKey.get(change.parityKey) ?? new Set<string>();
    for (const field of change.allowedFields) fields.add(field);
    expectedByKey.set(change.parityKey, fields);
    if (change.expectedPaintColors && change.expectedPaintColors.length > 0) {
      expectedPaintsByKey.set(change.parityKey, [
        ...(expectedPaintsByKey.get(change.parityKey) ?? []),
        ...change.expectedPaintColors,
      ]);
    }
  }
  for (const change of diff.changed) {
    const allowedFields = expectedByKey.get(change.parityKey);
    if (!allowedFields) {
      throw new Error(
        `Revision changed unexpected node ${change.parityKey} (${change.before.type} ${JSON.stringify(change.before.name)}): fields ${JSON.stringify(change.changedFields)} — before ${JSON.stringify(change.before)}, after ${JSON.stringify(change.after)}.`,
      );
    }
    const unexpectedFields = change.changedFields.filter(
      (field) => !allowedFields.has(field),
    );
    if (unexpectedFields.length > 0) {
      throw new Error(
        `Revision changed unexpected fields ${JSON.stringify(unexpectedFields)} on node ${change.parityKey}: before ${JSON.stringify(change.before)}, after ${JSON.stringify(change.after)}.`,
      );
    }
    assertExpectedPaintColors(
      change,
      expectedPaintsByKey.get(change.parityKey) ?? [],
    );
  }
  const changedKeys = new Set(diff.changed.map((change) => change.parityKey));
  const silentKeys = [...expectedByKey.keys()].filter(
    (parityKey) => !changedKeys.has(parityKey),
  );
  if (silentKeys.length > 0) {
    throw new Error(
      `Revision was expected to change nodes ${JSON.stringify(silentKeys)} but the parity snapshot shows no difference.`,
    );
  }
  return diff;
}

export interface LazyRevisionChangeIsolationResult {
  diff: SceneGraphParityDiff;
  /**
   * Whether the verdict came from the canonicalized before-side (the lazy
   * retry) rather than the raw before-side. `false` = fast path (raw isolated
   * on the first attempt, no write+reparse paid); `true` = the raw before-side
   * diverged on an unedited node and the canonicalized retry isolated the edit.
   */
  parityCanonicalizationApplied: boolean;
}

/**
 * Lazy parity-canonicalization backstop around {@link assertRevisionChangeIsolation}.
 *
 * The parity BEFORE-side is normally the raw parse of the source bytes — the
 * cheapest before-side and correct whenever the source already sits at
 * OpenPencil's writer-normalization fixpoint (everything OpenPencil itself
 * produced, and every synthetic fixture built by one export). It is INCORRECT
 * only for sources authored OUTSIDE OpenPencil (real customer `.fig` files):
 * the writer normalizes representation-only state on its FIRST serialize, so the
 * reopened after-side carries one normalization pass the raw before-side lacks
 * and an unedited node can look changed.
 *
 * The per-signature canonicalization in {@link buildSceneGraphParitySnapshot}
 * (float32 rounding, sparse/coalesced style runs, blend-mode equivalence) already
 * cancels the enumerated writer normalizations inline, on the fast path, with no
 * IO. This wrapper is the residual BACKSTOP for any writer-idempotent
 * normalization not covered by those inline rules (for example a sub-precision
 * `lineHeight` snap, which the typography signature compares verbatim): it runs
 * isolation against the RAW before-side first and, ONLY if that fails, recomputes
 * the before-side through the same writer (`canonicalizeBefore` — the
 * empirically-verified one-pass fixpoint) and re-runs isolation exactly ONCE. The
 * write+reparse cost is paid lazily, never on the fast path, and
 * `canonicalizeBefore` is invoked at most once.
 *
 * Fail-closed is preserved for every difference the PATCH could have caused: a
 * genuinely edited or tampered node diverges under both before-sides, because
 * canonicalizing the unedited pristine graph cannot manufacture a mutation the
 * patch made on the after-side. No field tolerance, no comparison weakening,
 * main's parity-key scheme unchanged — only the before-side SOURCE changes, and
 * only after the raw attempt has already failed.
 *
 * MEASURED LIMIT (do not read the paragraph above as a total-safety claim).
 * Canonicalization cancels EVERY deterministic one-pass writer difference —
 * including differences that are NOT representation-only. `[공통]
 * 고객정보등록(KYC)_UI.fig` loses/replaces three instance TEXT overrides on a
 * plain parse → export → reparse through `@open-pencil/core@0.13.2` alone (no
 * cogo code in the path; two independent runs produced the identical drift
 * list). That loss lands on the canonicalized before-side and on the after-side
 * alike, so it would cancel and let a lossy artifact pass as "isolated". The
 * retry therefore first runs {@link assertCanonicalizationPreservesAuthorText},
 * which refuses the whole revision when canonicalization moved author-visible
 * text. That guard is what makes the fail-closed claim above true; it is not a
 * fix for the underlying third-party round-trip loss.
 *
 * The write+reparse is INJECTED as `canonicalizeBefore` so this package needs no
 * `@open-pencil/core` dependency; each runtime (Node runner, browser worker)
 * supplies its own parse/prepare/export path while sharing this one lazy
 * contract.
 */
export async function assertRevisionChangeIsolationWithLazyCanonicalization<
  G extends OpenPencilParitySceneGraphLike,
>(input: {
  rawBefore: G;
  after: G;
  expectedChanges: ExpectedSceneGraphChange[];
  canonicalizeBefore: () => Promise<G> | G;
}): Promise<LazyRevisionChangeIsolationResult> {
  try {
    const diff = assertRevisionChangeIsolation(
      input.rawBefore,
      input.after,
      input.expectedChanges,
    );
    return { diff, parityCanonicalizationApplied: false };
  } catch {
    const canonicalBefore = await input.canonicalizeBefore();
    // Canonicalization is only allowed to normalize REPRESENTATION. If the one
    // export pass moved author-visible text, the same loss is present on the
    // after-side and would cancel silently — refuse instead of "verifying" a
    // lossy artifact.
    assertCanonicalizationPreservesAuthorText(input.rawBefore, canonicalBefore);
    // The retry verdict is FINAL: any throw here propagates (fail-closed).
    const diff = assertRevisionChangeIsolation(
      canonicalBefore,
      input.after,
      input.expectedChanges,
    );
    return { diff, parityCanonicalizationApplied: true };
  }
}

export const OPENPENCIL_SOURCE_EXPORT_TEXT_DRIFT =
  "OPENPENCIL_SOURCE_EXPORT_TEXT_DRIFT" as const;

export interface OpenPencilAuthorTextDriftEntry {
  sourceId: string | null;
  text: string;
  before: number;
  after: number;
}

export class OpenPencilSourceExportTextDriftError extends Error {
  readonly code = OPENPENCIL_SOURCE_EXPORT_TEXT_DRIFT;
  readonly lost: OpenPencilAuthorTextDriftEntry[];
  readonly gained: OpenPencilAuthorTextDriftEntry[];

  constructor(
    lost: OpenPencilAuthorTextDriftEntry[],
    gained: OpenPencilAuthorTextDriftEntry[],
  ) {
    super(
      `${OPENPENCIL_SOURCE_EXPORT_TEXT_DRIFT}: exporting this source loses author-visible text, so change isolation cannot certify a revision of it. Lost ${JSON.stringify(lost.slice(0, 5))}, gained ${JSON.stringify(gained.slice(0, 5))}.`,
    );
    this.name = "OpenPencilSourceExportTextDriftError";
    this.lost = lost;
    this.gained = gained;
  }
}

function authorTextCounts(
  graph: OpenPencilParitySceneGraphLike,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of graph.getAllNodes()) {
    if (typeof node.text !== "string" || node.text.length === 0) continue;
    const key = JSON.stringify([sourceIdentity(node), node.text]);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * Author-visible text multiset of two graphs, keyed by (source identity, text).
 * Coordinates are deliberately excluded — a moved label is a geometry question
 * the parity oracle already answers; this compares CONTENT only.
 */
export function diffOpenPencilAuthorTextContent(
  before: OpenPencilParitySceneGraphLike,
  after: OpenPencilParitySceneGraphLike,
): { lost: OpenPencilAuthorTextDriftEntry[]; gained: OpenPencilAuthorTextDriftEntry[] } {
  const beforeCounts = authorTextCounts(before);
  const afterCounts = authorTextCounts(after);
  const lost: OpenPencilAuthorTextDriftEntry[] = [];
  const gained: OpenPencilAuthorTextDriftEntry[] = [];
  const entry = (
    key: string,
    beforeCount: number,
    afterCount: number,
  ): OpenPencilAuthorTextDriftEntry => {
    const [sourceId, text] = JSON.parse(key) as [string | null, string];
    return { sourceId, text, before: beforeCount, after: afterCount };
  };
  for (const [key, count] of beforeCounts) {
    const other = afterCounts.get(key) ?? 0;
    if (other < count) lost.push(entry(key, count, other));
  }
  for (const [key, count] of afterCounts) {
    const other = beforeCounts.get(key) ?? 0;
    if (other < count) gained.push(entry(key, other, count));
  }
  return { lost, gained };
}

/**
 * Refuse a canonicalized before-side that is not content-equal to the raw one.
 *
 * The canonicalization backstop exists to cancel writer NORMALIZATION. It
 * cancels any deterministic one-pass difference, including content LOSS —
 * measured on `[공통] 고객정보등록(KYC)_UI.fig`, where a bare
 * `@open-pencil/core@0.13.2` parse → export → reparse replaces three instance
 * TEXT overrides. Without this assertion that loss appears on both parity
 * sides, cancels, and the revision reports `changeIsolationVerified: true`
 * while the written `.fig` has different labels than the customer's source.
 *
 * REACHABILITY (not "always on"): the retry is not taken by every real source —
 * `[공통] 영업점검색_UI.fig` isolates on the raw before-side
 * (`parityCanonicalizationApplied: false`). It IS taken whenever this drift
 * exists, because the same loss lands on the after-side and makes the raw
 * before-side diverge on a node the patch never targeted, which is exactly what
 * forces the retry. The one gap that leaves: a source whose ONLY drifted node is
 * also a declared edit target — there the raw attempt can pass and this guard
 * never runs; the intended value on that node is covered instead by the
 * runner's semantic readback (`assertOpenPencilFigRevisionReadback`).
 */
export function assertCanonicalizationPreservesAuthorText(
  rawBefore: OpenPencilParitySceneGraphLike,
  canonicalBefore: OpenPencilParitySceneGraphLike,
): void {
  const { lost, gained } = diffOpenPencilAuthorTextContent(
    rawBefore,
    canonicalBefore,
  );
  if (lost.length === 0 && gained.length === 0) return;
  throw new OpenPencilSourceExportTextDriftError(lost, gained);
}

/**
 * Node-targeted operations only. `set-variable-mode-value` is deliberately
 * ABSENT: it names no node, and its node-level effect is indirect — every paint
 * bound to the edited variable re-materializes on reopen. Those effects are
 * declared through {@link expectedParityChangesForPaintRematerializations},
 * which states the exact paint slot and colour rather than a field allowance.
 */
const PARITY_ALLOWED_FIELDS_BY_OPERATION: Record<string, string[]> = {
  "rename-node": ["name"],
  // set-text resets styleRuns, and TEXT auto-resize may legitimately
  // recompute the text frame extents on reopen.
  "set-text": ["text", "styleRunsSignature", "width", "height"],
  "set-visible": ["visible"],
  "set-opacity": ["opacity"],
  "set-geometry": ["x", "y", "width", "height", "rotation"],
  // set-solid-fill rewrites exactly one solid paint's color/opacity; it may
  // only surface through the fills signature.
  "set-solid-fill": ["fillsSignature"],
};

export function expectedParityChangesForOperations(
  operations: ReadonlyArray<{ operation: string; nodeId: string }>,
  keyByNodeId: Map<string, string>,
): ExpectedSceneGraphChange[] {
  return operations.map((operation) => {
    const parityKey = keyByNodeId.get(operation.nodeId);
    if (!parityKey) {
      throw new Error(
        `Revision operation targets node ${operation.nodeId} with no parity identity.`,
      );
    }
    const allowedFields =
      PARITY_ALLOWED_FIELDS_BY_OPERATION[operation.operation];
    if (!allowedFields) {
      throw new Error(
        `Revision operation ${operation.operation} has no parity contract.`,
      );
    }
    return { parityKey, allowedFields };
  });
}

/**
 * Parity expectations for the node-level effect of a `set-variable-mode-value`
 * edit: every paint bound to the edited variable re-materializes to the new
 * resolved colour when the exported file is reopened.
 *
 * The expectation is EXACT, not permissive — each entry names the paint slot and
 * the colour it must land on (see {@link ExpectedPaintColorChange}), so a node
 * that also moved for any other reason still fails isolation. Nodes are keyed by
 * the same parity identity as node-targeted operations; a bound node with no
 * parity identity is an error, never a skip.
 */
export function expectedParityChangesForPaintRematerializations(
  rematerializations: ReadonlyArray<{
    nodeId: string;
    channel: "fills" | "strokes";
    index: number;
    afterColorHex: string;
  }>,
  keyByNodeId: Map<string, string>,
): ExpectedSceneGraphChange[] {
  const byKey = new Map<string, ExpectedSceneGraphChange>();
  for (const rematerialization of rematerializations) {
    const parityKey = keyByNodeId.get(rematerialization.nodeId);
    if (!parityKey) {
      throw new Error(
        `Variable re-materialization targets node ${rematerialization.nodeId} with no parity identity.`,
      );
    }
    const field =
      rematerialization.channel === "fills"
        ? ("fillsSignature" as const)
        : ("strokesSignature" as const);
    const existing = byKey.get(parityKey) ?? {
      parityKey,
      allowedFields: [],
      expectedPaintColors: [],
    };
    if (!existing.allowedFields.includes(field)) {
      existing.allowedFields = [...existing.allowedFields, field];
    }
    existing.expectedPaintColors = [
      ...(existing.expectedPaintColors ?? []),
      {
        field,
        index: rematerialization.index,
        afterColorHex: rematerialization.afterColorHex,
      },
    ];
    byKey.set(parityKey, existing);
  }
  return [...byKey.values()];
}
